import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { profile } from "../../data/portfolio";

const links = [
  { label: "About", href: "#about" },
  { label: "Experience", href: "#experience" },
  { label: "Research", href: "#research" },
  { label: "Expertise", href: "#expertise" },
  { label: "Education", href: "#education" },
  { label: "Article", href: "#research" },
];

export function Monogram({ className = "w-9 h-9" }) {
  return (
    <svg viewBox="0 0 40 40" fill="none" className={className} aria-hidden="true">
      <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="1" />
      <path d="M12 14L16 26L20 14" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M20 14H24C26.2091 14 28 15.7909 28 18V22C28 24.2091 26.2091 26 24 26H20V14Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

export function scrollTo(hash) {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMenuOpen(false);
    scrollTo(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled || menuOpen ? "bg-paper/80 backdrop-blur-xl border-b border-stone/50" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-3 text-ink group"
        >
          <Monogram className="w-9 h-9 transition-transform duration-500 group-hover:rotate-[8deg]" />
          <span className="hidden sm:block font-display text-lg font-medium tracking-tight">Dr. {profile.name}</span>
        </button>
        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="text-sm font-medium text-fog hover:text-ink transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            data-testid="nav-contact-button"
            href={`mailto:${profile.email}`}
            className="inline-flex items-center rounded-full bg-ink text-paper px-5 md:px-6 py-2.5 text-sm font-medium tracking-wide transition-[background-color,transform] duration-300 hover:bg-[#3d453f] hover:-translate-y-0.5"
          >
            Get in touch
          </a>
          <button
            data-testid="nav-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className="lg:hidden glass w-11 h-11 !rounded-full flex items-center justify-center text-ink"
          >
            {menuOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden mx-4 mb-4 glass !rounded-3xl overflow-hidden"
          >
            <div className="px-7 py-6 flex flex-col">
              {links.map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 + i * 0.05 }}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-2xl font-light text-ink py-3.5 border-b border-stone/60 last:border-b-0"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
